/**
 * Phase 15.4.1 — 🎖️ "Now Featured" Social Share Card (config-driven)
 * --------------------------------------------------------------------
 * Three premium PNG layouts for artists to brag about a featured track:
 *   • story  — 1080 × 1920 (Instagram / TikTok story)
 *   • square — 1080 × 1080 (IG / Twitter / Facebook post)
 *   • wide   — 1600 × 900  (X header / website hero)
 *
 * Phase 15.4.1 — every text label, theme palette, and visibility toggle
 * is now driven by the admin-editable config from
 *   GET /api/share-cards/config
 * which the calling modal threads through as the `config` prop. When no
 * config is passed, the local DEFAULT_SHARE_CARD_CONFIG keeps the
 * production look pixel-identical (zero visual regression).
 *
 * Inline styles (no Tailwind) so html-to-image produces deterministic
 * bytes across browsers. Cover + avatar are passed in pre-baked as
 * data: URIs (CORS-safe pipeline reused from RecapStoryCard).
 */
import { forwardRef } from "react";
import { mergeConfig, resolveTheme } from "@/lib/shareCardThemes";

const fmt = (n) => Number(n || 0).toLocaleString();

const FONT_STACK = "Inter, ui-sans-serif, system-ui, -apple-system, 'Helvetica Neue', Arial";
const SHADOW_GLOW = "0 30px 80px rgba(236,72,153,0.30), 0 0 0 1px rgba(250,204,21,0.45) inset";

/** Build the radial-gradient bg from a theme palette. */
const bgFromTheme = (p) =>
  `radial-gradient(120% 80% at 10% 0%, ${p.bg_b} 0%, transparent 60%),` +
  `radial-gradient(110% 70% at 110% 100%, rgba(${p.glow_cool},0.7) 0%, transparent 65%),` +
  `linear-gradient(180deg, ${p.bg_a} 0%, ${p.bg_b} 60%, ${p.bg_c} 100%)`;

const Glow = ({ style, color, size = 700, alpha = 0.35 }) => (
  <div
    style={{
      position: "absolute", width: size, height: size, borderRadius: "50%",
      background: `radial-gradient(circle, rgba(${color},${alpha}) 0%, rgba(${color},0) 65%)`,
      filter: "blur(12px)", pointerEvents: "none", ...style,
    }}
  />
);

const Spotlight = () => (
  <div
    style={{
      position: "absolute", inset: 0,
      background:
        "linear-gradient(115deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.04) 100%)",
      pointerEvents: "none",
    }}
  />
);

const TitleRibbon = ({ text, gradient, textColor, size = 28, letterSpacing = 4 }) => (
  <div
    style={{
      display: "inline-flex", alignItems: "center",
      padding: "12px 28px", borderRadius: 999,
      background: gradient, color: textColor,
      fontWeight: 900, fontSize: size, letterSpacing,
      textTransform: "uppercase",
      boxShadow: "0 12px 40px rgba(250,204,21,0.40)",
      maxWidth: "100%",
    }}
  >
    {text}
  </div>
);

const MetricBadge = ({ icon, value, label, palette, compact = false }) => (
  <div
    style={{
      flex: 1, minWidth: 0,
      background: palette.metric_bg,
      border: `2px solid ${palette.metric_border}`,
      borderRadius: 22, padding: compact ? "16px 18px" : "20px 22px",
      boxShadow: "0 10px 26px rgba(0,0,0,0.35)",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: compact ? 24 : 28, color: palette.text_secondary }}>{icon}</span>
      <span style={{
        fontSize: compact ? 30 : 36, fontWeight: 900,
        color: palette.text_primary, letterSpacing: 0.5,
      }}>
        {fmt(value)}
      </span>
    </div>
    <div style={{
      marginTop: 4, fontSize: compact ? 14 : 16,
      color: palette.text_muted, opacity: 0.85,
      letterSpacing: 2, textTransform: "uppercase", fontWeight: 700,
    }}>
      {label}
    </div>
  </div>
);

const RgmCrest = ({ size = 30 }) => (
  <div
    style={{
      display: "inline-flex", alignItems: "center", gap: 12,
      padding: "10px 18px", borderRadius: 14,
      background: "rgba(0,0,0,0.5)", border: "1.5px solid rgba(250,204,21,0.55)",
      boxShadow: "0 6px 18px rgba(0,0,0,0.5)",
    }}
  >
    <span style={{ fontSize: size, fontWeight: 900, letterSpacing: 5, color: "#fde68a" }}>
      RGM STUDIO
    </span>
  </div>
);

const BadgePill = ({ kind, fontSize = 18 }) => {
  const meta = {
    founder:           { label: "👑 FOUNDER",          bg: "rgba(250,204,21,0.22)", fg: "#fde68a", bd: "rgba(250,204,21,0.55)" },
    founding_artist:   { label: "🌟 FOUNDING ARTIST",  bg: "rgba(250,204,21,0.22)", fg: "#fde68a", bd: "rgba(250,204,21,0.55)" },
    verified_artist:   { label: "✓ VERIFIED",          bg: "rgba(34,197,94,0.18)",  fg: "#86efac", bd: "rgba(34,197,94,0.55)" },
    spotlight_winner:  { label: "🏆 SPOTLIGHT WINNER", bg: "rgba(236,72,153,0.18)", fg: "#f9a8d4", bd: "rgba(236,72,153,0.55)" },
    spotlight_artist:  { label: "✨ SPOTLIGHT",         bg: "rgba(236,72,153,0.18)", fg: "#f9a8d4", bd: "rgba(236,72,153,0.55)" },
    playlist_pick:     { label: "🎧 PLAYLIST PICK",    bg: "rgba(168,85,247,0.18)", fg: "#c4b5fd", bd: "rgba(168,85,247,0.55)" },
    admin:             { label: "⚙ ADMIN",             bg: "rgba(168,85,247,0.18)", fg: "#c4b5fd", bd: "rgba(168,85,247,0.55)" },
  }[kind] || null;
  if (!meta) return null;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center",
        padding: "6px 14px", borderRadius: 999,
        background: meta.bg, color: meta.fg,
        border: `1.5px solid ${meta.bd}`,
        fontSize, fontWeight: 900, letterSpacing: 1.6,
        textTransform: "uppercase", whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
};

const FeaturedTagline = ({ text, palette, size = 22 }) => (
  <div
    style={{
      fontSize: size, fontWeight: 900, letterSpacing: 6,
      textTransform: "uppercase",
      background: palette.accent_ribbon,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      textShadow: "0 2px 14px rgba(250,204,21,0.35)",
    }}
  >
    {text}
  </div>
);

const CtaPill = ({ text, gradient, size = 22, padding = "10px 20px" }) => (
  <div
    style={{
      padding, borderRadius: 999,
      background: gradient, color: "white",
      fontSize: size, fontWeight: 900, letterSpacing: 3,
      textTransform: "uppercase",
      boxShadow: "0 8px 24px rgba(236,72,153,0.4)",
    }}
  >
    {text}
  </div>
);

/** Pick the right cover/avatar source given the config + baked URIs.
 *  Empty result = visibility toggle disabled → caller should skip render. */
const resolveImage = (configValue, baked, raw, fallback) => {
  if (!configValue) return null;
  return baked || raw || fallback || null;
};


/** ───────────────── STORY (1080×1920) ───────────────── */
const StoryLayout = forwardRef(({ data, bakedCover, bakedAvatar, config }, ref) => {
  const cfg = mergeConfig(config);
  const theme = resolveTheme(cfg.theme);
  const p = theme.palette;
  const cover = resolveImage(cfg.show_cover, bakedCover, data.cover, cfg.fallback_image_url);
  const avatar = resolveImage(cfg.show_avatar, bakedAvatar, data.avatar, null);

  return (
    <div
      ref={ref}
      data-testid="now-featured-card-story"
      style={{
        width: 1080, height: 1920, position: "relative",
        background: bgFromTheme(p),
        color: p.text_primary, fontFamily: FONT_STACK,
        overflow: "hidden", boxSizing: "border-box",
      }}
    >
      <Glow style={{ top: -200, right: -120 }} color={p.glow_warm} size={700} alpha={0.4} />
      <Glow style={{ bottom: -200, left: -160 }} color={p.glow_cool} size={800} alpha={0.4} />
      <Spotlight />

      <div style={{ position: "absolute", top: 70, left: 70, right: 70, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <TitleRibbon text={cfg.title_text} gradient={p.accent_ribbon} textColor="#1a0a00" />
        <RgmCrest />
      </div>

      {/* Cover tile */}
      {cover ? (
        <div
          style={{
            position: "absolute", top: 220, left: 90, right: 90, height: 900,
            borderRadius: 40, overflow: "hidden",
            boxShadow: SHADOW_GLOW, background: "#1a0a26",
          }}
        >
          <img src={cover} alt={data.track_title} crossOrigin="anonymous"
               style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 28%, rgba(0,0,0,0.6) 70%, rgba(0,0,0,0.9) 100%)" }} />
          <div style={{ position: "absolute", left: 36, right: 36, bottom: 36, display: "flex", alignItems: "center", gap: 22 }}>
            {avatar && (
              <img src={avatar} alt="" crossOrigin="anonymous"
                   style={{
                     width: 96, height: 96, borderRadius: "50%",
                     objectFit: "cover",
                     border: `3px solid ${p.ring_color}`,
                     flexShrink: 0,
                   }} />
            )}
            <div style={{ minWidth: 0 }}>
              <FeaturedTagline text={cfg.tagline_text} palette={p} size={22} />
              <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1.05, color: p.text_primary, textShadow: "0 4px 18px rgba(0,0,0,0.55)", marginTop: 4 }}>
                {data.track_title}
              </div>
              <div style={{ marginTop: 8, fontSize: 30, fontWeight: 700, color: p.text_secondary }}>
                @{data.artist_handle}
              </div>
              {cfg.show_badges && (data.badges && data.badges.length > 0) && (
                <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {data.badges.map((b) => <BadgePill key={b} kind={b} fontSize={18} />)}
                </div>
              )}
            </div>
          </div>
          <div style={{ position: "absolute", top: 28, right: 28 }}>
            <CtaPill text={cfg.cta_text} gradient={p.accent_pill} size={22} padding="10px 20px" />
          </div>
        </div>
      ) : (
        /* Cover hidden — render the title block in the same vertical slot so
           the metric/footer geometry doesn't break. */
        <div style={{ position: "absolute", top: 380, left: 90, right: 90, textAlign: "center" }}>
          <FeaturedTagline text={cfg.tagline_text} palette={p} size={36} />
          <div style={{ marginTop: 24, fontSize: 96, fontWeight: 900, color: p.text_primary, lineHeight: 1.05 }}>
            {data.track_title}
          </div>
          <div style={{ marginTop: 16, fontSize: 40, color: p.text_secondary, fontWeight: 700 }}>
            @{data.artist_handle}
          </div>
        </div>
      )}

      {cfg.show_stats && (
        <div style={{ position: "absolute", top: 1190, left: 90, right: 90, display: "flex", gap: 18 }}>
          <MetricBadge icon="▶" value={data.display_plays} label="Plays" palette={p} compact />
          <MetricBadge icon="🏆" value={data.reach} label="Reach" palette={p} compact />
          <MetricBadge icon="👁" value={data.impressions} label="Impressions" palette={p} compact />
        </div>
      )}

      {cfg.show_watermark && (
        <div style={{ position: "absolute", top: 1430, left: 90, right: 90, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: p.text_secondary, letterSpacing: 3, textTransform: "uppercase" }}>
            {cfg.watermark_text}
          </div>
          <div style={{ marginTop: 14, fontSize: 22, color: p.text_muted, opacity: 0.85 }}>
            Tap the link in bio · Stream + react + share
          </div>
        </div>
      )}

      <div style={{ position: "absolute", bottom: 90, left: 0, right: 0, textAlign: "center" }}>
        <div style={{ fontSize: 22, color: p.text_muted, opacity: 0.85, letterSpacing: 1, marginBottom: 18 }}>
          {cfg.footer_text || data.share_url || "rgmstudio.org"}
        </div>
        <RgmCrest size={26} />
      </div>
    </div>
  );
});
StoryLayout.displayName = "NowFeaturedStoryLayout";


/** ───────────────── SQUARE (1080×1080) ───────────────── */
const SquareLayout = forwardRef(({ data, bakedCover, bakedAvatar, config }, ref) => {
  const cfg = mergeConfig(config);
  const theme = resolveTheme(cfg.theme);
  const p = theme.palette;
  const cover = resolveImage(cfg.show_cover, bakedCover, data.cover, cfg.fallback_image_url);
  const avatar = resolveImage(cfg.show_avatar, bakedAvatar, data.avatar, null);

  return (
    <div
      ref={ref}
      data-testid="now-featured-card-square"
      style={{
        width: 1080, height: 1080, position: "relative",
        background: bgFromTheme(p),
        color: p.text_primary, fontFamily: FONT_STACK,
        overflow: "hidden", boxSizing: "border-box",
        display: "flex", alignItems: "stretch",
      }}
    >
      <Glow style={{ top: -120, left: -120 }} color={p.glow_warm} size={500} alpha={0.35} />
      <Glow style={{ bottom: -120, right: -120 }} color={p.glow_cool} size={520} alpha={0.35} />
      <Spotlight />

      {cover && (
        <div style={{ flex: "0 0 520px", padding: 40, position: "relative" }}>
          <div style={{ width: "100%", height: "100%", borderRadius: 32, overflow: "hidden", boxShadow: SHADOW_GLOW, background: "#1a0a26", position: "relative" }}>
            <img src={cover} alt={data.track_title} crossOrigin="anonymous"
                 style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{ position: "absolute", top: 20, right: 20 }}>
              <CtaPill text={cfg.cta_text} gradient={p.accent_pill} size={18} padding="8px 16px" />
            </div>
          </div>
        </div>
      )}

      <div style={{ flex: 1, padding: cover ? "44px 56px 44px 0" : "44px 56px", display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
        <div>
          <TitleRibbon text={cfg.title_text} gradient={p.accent_ribbon} textColor="#1a0a00" size={20} letterSpacing={2.5} />
          <div style={{ marginTop: 30, fontSize: 56, fontWeight: 900, lineHeight: 1.05, color: p.text_primary }}>
            {data.track_title}
          </div>
          {avatar ? (
            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 14 }}>
              <img src={avatar} alt="" crossOrigin="anonymous"
                   style={{ width: 60, height: 60, borderRadius: "50%", objectFit: "cover", border: `3px solid ${p.ring_color}` }} />
              <div style={{ fontSize: 30, fontWeight: 700, color: p.text_secondary }}>@{data.artist_handle}</div>
            </div>
          ) : (
            <div style={{ marginTop: 14, fontSize: 30, fontWeight: 700, color: p.text_secondary }}>@{data.artist_handle}</div>
          )}
          {cfg.show_badges && (data.badges && data.badges.length > 0) && (
            <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {data.badges.map((b) => <BadgePill key={b} kind={b} fontSize={16} />)}
            </div>
          )}
        </div>
        {cfg.show_stats && (
          <div style={{ display: "flex", gap: 18 }}>
            <MetricBadge icon="🏆" value={data.reach} label="Reach" palette={p} />
            <MetricBadge icon="👁" value={data.impressions} label="Impressions" palette={p} />
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 20, color: p.text_muted, opacity: 0.85 }}>
            {cfg.footer_text || data.share_url || "rgmstudio.org"}
          </div>
          <RgmCrest size={20} />
        </div>
      </div>
    </div>
  );
});
SquareLayout.displayName = "NowFeaturedSquareLayout";


/** ───────────────── WIDE (1600×900) ───────────────── */
const WideLayout = forwardRef(({ data, bakedCover, bakedAvatar, config }, ref) => {
  const cfg = mergeConfig(config);
  const theme = resolveTheme(cfg.theme);
  const p = theme.palette;
  const cover = resolveImage(cfg.show_cover, bakedCover, data.cover, cfg.fallback_image_url);
  const avatar = resolveImage(cfg.show_avatar, bakedAvatar, data.avatar, null);

  return (
    <div
      ref={ref}
      data-testid="now-featured-card-wide"
      style={{
        width: 1600, height: 900, position: "relative",
        background: bgFromTheme(p),
        color: p.text_primary, fontFamily: FONT_STACK,
        overflow: "hidden", boxSizing: "border-box",
        display: "flex", alignItems: "stretch",
      }}
    >
      <Glow style={{ top: -160, left: -160 }} color={p.glow_warm} size={620} alpha={0.4} />
      <Glow style={{ bottom: -200, right: -200 }} color={p.glow_cool} size={720} alpha={0.4} />
      <Spotlight />

      {cover && (
        <div style={{ flex: "0 0 700px", padding: 60, position: "relative" }}>
          <div style={{ width: "100%", height: "100%", borderRadius: 36, overflow: "hidden", boxShadow: SHADOW_GLOW, background: "#1a0a26", position: "relative" }}>
            <img src={cover} alt={data.track_title} crossOrigin="anonymous"
                 style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{ position: "absolute", top: 22, right: 22 }}>
              <CtaPill text={cfg.cta_text} gradient={p.accent_pill} size={20} padding="10px 22px" />
            </div>
          </div>
        </div>
      )}

      <div style={{ flex: 1, padding: cover ? "70px 70px 70px 0" : "70px", display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
        <div>
          <TitleRibbon text={cfg.title_text} gradient={p.accent_ribbon} textColor="#1a0a00" size={22} letterSpacing={3} />
          <div style={{ marginTop: 40, fontSize: 76, fontWeight: 900, lineHeight: 1.02, color: p.text_primary }}>
            {data.track_title}
          </div>
          {avatar ? (
            <div style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 18 }}>
              <img src={avatar} alt="" crossOrigin="anonymous"
                   style={{ width: 76, height: 76, borderRadius: "50%", objectFit: "cover", border: `3px solid ${p.ring_color}` }} />
              <div style={{ fontSize: 36, fontWeight: 700, color: p.text_secondary }}>@{data.artist_handle}</div>
            </div>
          ) : (
            <div style={{ marginTop: 24, fontSize: 36, fontWeight: 700, color: p.text_secondary }}>@{data.artist_handle}</div>
          )}
          {cfg.show_badges && (data.badges && data.badges.length > 0) && (
            <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {data.badges.map((b) => <BadgePill key={b} kind={b} fontSize={18} />)}
            </div>
          )}
        </div>
        {cfg.show_stats && (
          <div style={{ display: "flex", gap: 22 }}>
            <MetricBadge icon="🏆" value={data.reach} label="Boosted Reach" palette={p} />
            <MetricBadge icon="👁" value={data.impressions} label="Impressions" palette={p} />
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 24, color: p.text_muted, opacity: 0.85, letterSpacing: 1 }}>
            {cfg.footer_text || data.share_url || "rgmstudio.org"}
          </div>
          <RgmCrest size={24} />
        </div>
      </div>
    </div>
  );
});
WideLayout.displayName = "NowFeaturedWideLayout";


/** Default export — switcher that picks the right layout. */
const NowFeaturedCard = forwardRef(({ layout = "story", data, bakedCover, bakedAvatar, config }, ref) => {
  if (!data) return null;
  if (layout === "square") return <SquareLayout ref={ref} data={data} bakedCover={bakedCover} bakedAvatar={bakedAvatar} config={config} />;
  if (layout === "wide")   return <WideLayout   ref={ref} data={data} bakedCover={bakedCover} bakedAvatar={bakedAvatar} config={config} />;
  return                            <StoryLayout  ref={ref} data={data} bakedCover={bakedCover} bakedAvatar={bakedAvatar} config={config} />;
});
NowFeaturedCard.displayName = "NowFeaturedCard";

export default NowFeaturedCard;
export { StoryLayout, SquareLayout, WideLayout };
